import type { DataProvider, GetListParams, RaRecord } from "react-admin";

import { DEFAULT_MAX_RECORDS, PAGE_CHUNK } from "./paginationLimits";

export interface FetchListAllOptions {
  /** Hard cap on total records returned. Defaults to DEFAULT_MAX_RECORDS. */
  maxRecords?: number;
  /** Rows per request. Defaults to PAGE_CHUNK (the server's per-page cap). */
  chunkSize?: number;
  /** Called when the result set was cut off (at maxRecords, or because paging
   *  ended with fewer rows than the reported total). */
  onTruncated?: (fetched: number, total: number) => void;
}

export interface FetchListAllResult<RecordType extends RaRecord = RaRecord> {
  data: RecordType[];
  total: number;
  truncated: boolean;
}

/**
 * Fetch a list resource in server-sized page chunks until the reported total
 * (or maxRecords) is reached. Replaces the single perPage:50000 request pattern
 * (#5166) — pages are fetched sequentially, never in parallel, so a bulk read
 * holds at most one in-flight page. Mirrors Aura's fetchListAll (ADO 5703).
 */
export async function fetchListAll<RecordType extends RaRecord = RaRecord>(
  dataProvider: DataProvider,
  resource: string,
  params: Partial<Omit<GetListParams, "pagination">> = {},
  options: FetchListAllOptions = {}
): Promise<FetchListAllResult<RecordType>> {
  const { maxRecords = DEFAULT_MAX_RECORDS, chunkSize = PAGE_CHUNK, onTruncated } = options;

  const data: RecordType[] = [];
  let total = 0;
  let page = 1;
  const maxPages = Math.max(1, Math.ceil(maxRecords / chunkSize));

  for (;;) {
    const { data: pageData, total: pageTotal } = await dataProvider.getList<RecordType>(resource, {
      ...params,
      pagination: { page, perPage: chunkSize },
    } as GetListParams);
    total = pageTotal ?? 0;
    data.push(...pageData);

    const target = Math.min(total, maxRecords);
    // Stop on: target reached, a short page (total drifted under us while
    // paging), or the page budget spent — guards against infinite loops.
    if (data.length >= target || pageData.length < chunkSize || page >= maxPages) {
      break;
    }
    page += 1;
  }

  const result = data.length > maxRecords ? data.slice(0, maxRecords) : data;
  // Truncation is ANY case where we return fewer rows than the caller asked for:
  //  - the reported total exceeds the maxRecords ceiling, OR
  //  - paging ended early (short page / page budget) with rows still outstanding.
  // The second case guards against a silent partial file when an endpoint omits
  // a real total (fallback total = page length) or returns short pages for any
  // reason other than genuine exhaustion. Never fail silent-but-incomplete.
  const expected = Math.min(total, maxRecords);
  const truncated = total > maxRecords || result.length < expected;
  if (truncated) {
    console.warn(`[fetchListAll] ${resource}: truncated at ${result.length} of ${total} records`);
    onTruncated?.(result.length, total);
  }

  return { data: result, total, truncated };
}
